import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Box, IconButton, Grid } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { styled } from '@mui/system';
import SpaceCard from 'components/pages/booking/bookingFind/spaceCard';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';

// Styled container for the whole slider
const VenueSliderContainer = styled(Box)(() => ({
  padding: '20px',
  backgroundColor: '#f5f5f5',
  marginTop: '40px',
  position: 'relative', // For positioning arrows
  overflow: 'hidden', // Hide overflow for smooth scrolling
}));

// Horizontal scrolling container
const SliderWrapper = styled(Box)(() => ({
  display: 'flex',
  transition: 'transform 0.3s ease-in-out', // Smooth scroll transition
}));

// Left and right navigation arrows
const ArrowButton = styled(IconButton)(() => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 1,
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
}));

const VenueSlider = ({ navigateVenue, venues, showMore = true }) => {
  const sliderRef = useRef(null);
  const listItemRefs = useRef([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const navigate = useNavigate();
  const [startDate] = useState(dayjs().startOf('week'));
  const endDate = startDate.add(4, 'day');
  const [selectedSpace] = useState(null);
  const [,setSelectedBooking] = useState(null);


  const handleScrollLeft = () => {
    if (sliderRef.current) {
      const newPosition = Math.max(scrollPosition - 300, 0); // Scroll 300px left
      setScrollPosition(newPosition);
      sliderRef.current.scrollTo({ left: newPosition, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (sliderRef.current) {
      const maxScroll = sliderRef.current.scrollWidth - sliderRef.current.clientWidth;
      const newPosition = Math.min(scrollPosition + 300, maxScroll); // Scroll 300px right
      setScrollPosition(newPosition);
      sliderRef.current.scrollTo({ left: newPosition, behavior: 'smooth' });
    }
  };

  const handleCardClick = (slug) => {
    navigateVenue(slug);
  };

  const navigateAll = () => {
    navigate(`/booking-find`);
  };


  const dateClicked = (space, date) => {
    setSelectedBooking({ space, date });
  };


  return (
    <VenueSliderContainer>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ marginBottom: '20px' }}>
        <Grid item>
          <Typography variant="h5" component="h2" color="primary" sx={{ textAlign: 'left', fontWeight: 'bold' }}>
            Book again
          </Typography>
        </Grid>
        {showMore && <Grid item>
          <Typography
            variant="body2"
            sx={{ cursor: 'pointer', color: 'primary.main', fontSize: '14px' }}
            onClick={navigateAll}
          >
            See all
          </Typography>
        </Grid>}
      </Grid>

      {/* Left Arrow */}
      {scrollPosition > 0 && (
        <ArrowButton onClick={handleScrollLeft} sx={{ left: '10px' }}>
          <ArrowBackIosIcon />
        </ArrowButton>
      )}

      {/* Horizontal scrollable content */}
      <Box
        sx={{ overflowX: 'auto', display: 'flex' }}
        ref={sliderRef}
      >
        <SliderWrapper>
          {venues.map((space, index) => {

            return (
              <Box sx={{ 
                margin: '10px',
                width: '400px',
                }} key={index}>
                <SpaceCard
                  space={space}
                  setSelectedSpace={space => handleCardClick(space.slug)}
                  startDate={startDate}
                  endDate={endDate}
                  onDetailClick={(space, date) => dateClicked(space, date)}
                  onMouseEnter={() => { }}
                  onMouseLeave={() => { }}
                  listItemRefs={listItemRefs}
                  isHovered={false}
                  isSelected={selectedSpace?.slug === space.slug}
                />
              </Box>
            );
          })}
        </SliderWrapper>
      </Box>

      {/* Right Arrow */}
      {sliderRef.current && scrollPosition < sliderRef.current.scrollWidth - sliderRef.current.clientWidth && (
        <ArrowButton onClick={handleScrollRight} sx={{ right: '10px' }}>
          <ArrowForwardIosIcon />
        </ArrowButton>
      )}
    </VenueSliderContainer>
  );
};

export default VenueSlider;
